// src/app/olusturUrl.ts
//
// OLUŞTURMA (composer/kamera) + CHECK-IN URL'İ — tek kaynak.
//
// ── NEDEN VAR ─────────────────────────────────────────────────────────────
// ÖLÇÜLDÜ (bu tur): `grep -c 'compose\|create' src/app/appRouting.ts` → 0.
// Composer/kamera ve check-in akışlarının HİÇ URL'si yoktu:
//   · composer açıkken sayfa yenilenince modal kapanıyordu,
//   · geri tuşu composer'ı kapatmıyor, altındaki sayfadan çıkıyordu,
//   · "şu mekâna check-in yap" bağlantısı paylaşılamıyordu.
// CLAUDE.md'deki kalıcı kural bunun tersini istiyor: her görünüm kendi URL'ini
// üretmeli, cold-load çalışmalı, `appRouting.resolvePathToPage`'de matcher olmalı.
//
// ── NEDEN AYRI MODÜL (appRouting'in içine gömülmedi) ──────────────────────
// Aynı bilgi ÜÇ yerde lazım: (1) matcher — URL → modal, (2) açılış — modal →
// URL yazımı (App.tsx sekmesi + CreateMenu seçimi + mekân seçimi), (3) kapanış
// temizliği (appModals.closeModal). Üç yere üç kopya yazmak, bu repoda beş kez
// kapatılmış sınıfı (hasTurnServer · venueClaim · generateSessionId · mekân
// duvarı · sohbet URL'i) altıncı kez açardı.
//
// 🧭 KANON (sohbetUrl.ts'ten devralındı): *iki yerde yazılan aynı kural, iki
//    farklı kuraldır — henüz değil, ama er ya da geç.*
//
// ── NEDEN `push` (replace DEĞİL) ──────────────────────────────────────────
// Projedeki kural: "modlar replace, katmanlar push" (myclipsv3/feed/
// discoveryUrl.ts:108). Composer, altındaki sayfanın bir MODU değil, ÜSTÜNE
// açılan bir KATMAN → push. Böylece geri tuşu composer'ı kapatır, altındaki
// sayfayı değil. Tek istisna aşağıda: permalink (/p,/c) üstünden açılışta
// REPLACE — DM (`/direct`) ve bildirimlerin (`/notifications`) ölçülmüş deseni:
// aksi halde geri tuşu kapanan composer'ın altında klip/post modalını YENİDEN
// açardı.
//
// ── NEDEN TEK YENİ REZERVE KELİME ("create") ──────────────────────────────
// `reservedUsernames.test.ts`, `TUM_REZERVE`nin `functions/src/auth.ts` ve
// `firestore.rules` kopyalarıyla BİREBİR EŞİT olmasını zorluyor; yeni bir
// üst-seviye rota kelimesi üç dosyayı birden değiştirmek demek. Bu yüzden
// check-in için AYRI bir kök (`/checkin`) AÇILMADI — check-in zaten "+"
// menüsünün bir dalı, yani bir OLUŞTURMA akışı → `/create/checkin`.
// İki kelime yerine bir kelime; rules/functions'a tek satır dokunuldu.
//
// ⚠️ Bu modül SAF: DOM'a yalnız `window.history` üzerinden ve YALNIZ
//    `olusturUrlYaz`/`olusturUrlTemizle` içinden dokunur; çözümleyiciler
//    (`yoldanOlustur`, `olusturYolu`) hiçbir yan etki üretmez → `olusturUrl.test.ts`
//    ile emülatörsüz kanıtlanır.

/** Oluşturma kökü. `ROUTER_REZERVE`ye "create" olarak eklendi (tek kelime). */
export const OLUSTUR_KOK = "/create";

/** `modalContent` değerleri — appModals.renderModal'ın tanıdığı adlar. */
export type OlusturModali =
  | "createMenu"
  | "newpost"
  | "newclip"
  | "checkin"
  | "newCheckInDetail";

/** Bir oluşturma görünümünün TAM durumu (URL ile birebir çevrilebilir). */
export type OlusturDurumu = {
  modal: OlusturModali;
  /** Yalnız `newclip`: `/create/camera` → composer kamerayla açılır. */
  kamera?: boolean;
  /** Yalnız `newCheckInDetail`: seçilen mekânın Google place id'si. */
  placeId?: string;
};

/**
 * Oluşturma katmanına ait `modalContent` adlarının TAM listesi.
 * (Kapanış temizliği + popstate kararı bunu kullanır; elle yazılan ikinci bir
 * liste ÇÜRÜR — `olusturYolu` bu adların hepsini tanımak ZORUNDA.)
 */
export const OLUSTURMA_MODALLARI: readonly OlusturModali[] = [
  "createMenu",
  "newpost",
  "newclip",
  "checkin",
  "newCheckInDetail",
];

/** `modalContent` bir oluşturma katmanı mı? (null/undefined güvenli) */
export function olusturModaliMi(icerik: unknown): boolean {
  return (
    typeof icerik === "string" &&
    (OLUSTURMA_MODALLARI as readonly string[]).includes(icerik)
  );
}

/**
 * Check-in detayına geçen mekân yükü — `CheckInModal.onPlaceSelect`ten ÖLÇÜLDÜ.
 *
 * `lat`/`lng` DAHİL: paylaşım adımı (`NewCheckInDetail{Desktop,Mobile}`)
 * `placeCoords: [lng,lat]` olmadan check-in'i REDDEDİYOR. Yük koordinatsız
 * saklansaydı yenilemeden sonra geri gelen detay ekranı GÖRÜNÜR ama
 * PAYLAŞILAMAZ olurdu — "aynı URL, farklı içerik"in sinsi kardeşi: aynı
 * ekran, ÇALIŞMAYAN içerik.
 */
export type MekanYuku = {
  id?: string;
  name?: string;
  address?: string;
  lat?: number;
  lng?: number;
};

/** `history.state` içinde mekân yükünü taşıyan anahtar. */
export const OLUSTUR_STATE_ANAHTARI = "__olustur";

type OlusturState = {
  [OLUSTUR_STATE_ANAHTARI]?: true;
  mekan?: MekanYuku | null;
};

/** `history.state`e yalnız SAYI konur (LatLng metodu structured-clone'lanamaz). */
function sayiyaCevir(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

// ── URL üretimi ───────────────────────────────────────────────────────────

/**
 * Durum → yol. Bilinmeyen modal için `null` (çağıran URL yazmaz).
 * `placeId` encode edilir (`/place/{id}` deseniyle hizalı).
 */
export function olusturYolu(durum: OlusturDurumu | null | undefined): string | null {
  if (!durum || !durum.modal) return null;
  switch (durum.modal) {
    case "createMenu":
      return OLUSTUR_KOK;
    case "newpost":
      return `${OLUSTUR_KOK}/post`;
    case "newclip":
      return durum.kamera ? `${OLUSTUR_KOK}/camera` : `${OLUSTUR_KOK}/clip`;
    case "checkin":
      return `${OLUSTUR_KOK}/checkin`;
    case "newCheckInDetail": {
      const id = String(durum.placeId || "").trim();
      // placeId YOKSA detay yolu ÜRETİLEMEZ (o URL cold-load'da hiçbir şeye
      // çözülmez). Bir üst adıma — mekân seçiciye — düşülür.
      if (!id) return `${OLUSTUR_KOK}/checkin`;
      return `${OLUSTUR_KOK}/checkin/${encodeURIComponent(id)}`;
    }
    default:
      return null;
  }
}

// ── URL çözümleme (matcher) ───────────────────────────────────────────────

// Trailing-slash `\/?` her kolda var — PR-V3b'de `/c/{id}/`ın hiçbir matcher'a
// düşmemesi bu dersi bırakmıştı.
const YOL_MENU = /^\/create\/?$/;
const YOL_POST = /^\/create\/post\/?$/;
const YOL_CLIP = /^\/create\/clip\/?$/;
const YOL_KAMERA = /^\/create\/camera\/?$/;
const YOL_CHECKIN = /^\/create\/checkin\/?$/;
const YOL_CHECKIN_MEKAN = /^\/create\/checkin\/([^/]+)\/?$/;

/** Yol → durum. Oluşturma yolu değilse `null`. */
export function yoldanOlustur(path: string | null | undefined): OlusturDurumu | null {
  const p = String(path || "");
  if (YOL_MENU.test(p)) return { modal: "createMenu" };
  if (YOL_POST.test(p)) return { modal: "newpost" };
  if (YOL_CLIP.test(p)) return { modal: "newclip" };
  if (YOL_KAMERA.test(p)) return { modal: "newclip", kamera: true };
  if (YOL_CHECKIN.test(p)) return { modal: "checkin" };
  const m = p.match(YOL_CHECKIN_MEKAN);
  if (m) {
    let placeId = m[1];
    try {
      placeId = decodeURIComponent(placeId);
    } catch (_) {
      /* bozuk yüzde-kodlaması → ham hâli kullanılır (çökme yok) */
    }
    return { modal: "newCheckInDetail", placeId };
  }
  return null;
}

/** Verilen yol bir oluşturma yolu mu? (kapanış temizliği + popstate kararı) */
export function olusturYolundaMi(path: string | null | undefined): boolean {
  return yoldanOlustur(path) !== null;
}

// ── history.state ─────────────────────────────────────────────────────────
//
// NEDEN state: `/create/checkin/{placeId}` cold-load'da yalnız placeId taşır —
// mekân ADI ve adresi URL'de YOK. Ama `history.state` sayfa YENİLENSE DE aynı
// history girişinde yaşar; yani F5 ve geri/ileri TAM veriyle döner.
//
// ⚠️ DÜRÜSTLÜK SINIRI: YABANCI bir sekmede açılan paylaşım linkinde state
// YOKTUR — orada elde yalnız placeId kalır. İsim UYDURULMAZ (#783: id isim
// yerine geçmez) ve Google'dan hidrasyon BU TURDA YAPILMADI. Yani
// `/create/checkin/{placeId}` üçüncü kişide detay ekranını açar, ama mekân
// adı boş ve koordinat yok → paylaşım adımı uyarı verir. Bu sınır
// `/place/{id}` cold-load'unun ölçülmüş sınırıyla AYNI ("soğuk açılışta
// yalnız placeId var … soğuk linkte isim eksik olabilir", appRouting mPlace).

/** `history.state`ten mekân yükünü çıkarır; yoksa `null` (asla çökmez). */
export function mekaniStatedenOku(state: unknown): MekanYuku | null {
  if (!state || typeof state !== "object") return null;
  const s = state as OlusturState;
  if (!s[OLUSTUR_STATE_ANAHTARI]) return null;
  const mekan = s.mekan;
  if (!mekan || typeof mekan !== "object") return null;
  return mekan;
}

/** `history.state`e yazılacak yük (yalnız serileşebilir alanlar). */
export function olusturStateYap(mekan?: MekanYuku | null): OlusturState {
  const s: OlusturState = { [OLUSTUR_STATE_ANAHTARI]: true };
  if (mekan && typeof mekan === "object") {
    // ⚠️ Nesnenin TAMAMI değil ÜÇ ALAN: `history.state` structured-clone'lanır;
    // Google `Place` örneği (fonksiyon taşır) DataCloneError verirdi.
    s.mekan = {
      id: typeof mekan.id === "string" ? mekan.id : undefined,
      name: typeof mekan.name === "string" ? mekan.name : undefined,
      address: typeof mekan.address === "string" ? mekan.address : undefined,
      lat: sayiyaCevir(mekan.lat),
      lng: sayiyaCevir(mekan.lng),
    };
  }
  return s;
}

// ── URL yazımı ────────────────────────────────────────────────────────────

/**
 * Oluşturma URL'ini yazar.
 *
 * @param durum   hedef görünüm
 * @param secim.degistir  `true` → replaceState (permalink üstü / aynı katmanda
 *                        mod değişimi), `false` → pushState (yeni katman)
 * @param secim.mekan     `newCheckInDetail` için history.state'e konacak yük
 * @returns yazılan yol ya da `null` (yol üretilemedi / zaten oradayız)
 */
export function olusturUrlYaz(
  durum: OlusturDurumu,
  { degistir = false, mekan = null }: { degistir?: boolean; mekan?: MekanYuku | null } = {},
): string | null {
  const yol = olusturYolu(durum);
  if (!yol) return null;
  try {
    if (typeof window === "undefined") return null;
    const state = olusturStateYap(mekan);
    if (window.location.pathname === yol) {
      // Aynı yoldayız: yeni giriş AÇMA (history şişer + geri tuşu kilitlenir),
      // ama state'i tazele (mekân yükü kaybolmasın).
      window.history.replaceState(state, "", yol);
      return null;
    }
    if (degistir) window.history.replaceState(state, "", yol);
    else window.history.pushState(state, "", yol);
    return yol;
  } catch (_) {
    /* no-op — URL yansıması hiçbir zaman akışı kırmamalı */
    return null;
  }
}

/**
 * Composer kapanışında URL'i temizler.
 *
 * NEDEN `replaceState` (history.back DEĞİL): bildirimlerin ölçülmüş deseni
 * (appModals:224 "history.back belirsizliği yok"). `back()` asenkron ve
 * composer birden çok giriş açmış olabilir (`/create` → `/create/clip`);
 * kaç adım geri gideceğini bilmek imkânsız. `replaceState` MEVCUT girişi
 * temizler → yenilemede composer geri gelmez.
 *
 * @returns temizlik yapıldıysa `true`
 */
export function olusturUrlTemizle(): boolean {
  try {
    if (typeof window === "undefined") return false;
    if (!olusturYolundaMi(window.location.pathname)) return false;
    window.history.replaceState({}, "", "/");
    return true;
  } catch (_) {
    return false;
  }
}
